import { useState, memo } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { MapPickerWithRadius } from "@/components/MapPickerWithRadius";
import { ProductSelectItems } from "@/components/ProductSelectItems";
import type { ProductCatalogOption } from "@/types/ProductCatalog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Trash2, ChevronDown, ChevronUp } from "lucide-react";
import {
  DEFAULT_FILTERS_OPERATOR,
  type FiltersOperator,
} from "@/lib/contactFilterApi";
import { RecipientPhoneCsvUpload } from "./RecipientPhoneCsvUpload";

type FilterOperator = {
  value: string;
  label: string;
  inputType?: "number" | "text" | "select" | "date";
  inputLabel?: string;
  inputUnit?: string;
};

type FilterType = {
  value: string;
  label: string;
  operators: FilterOperator[];
};

const TEXT_FIELD_FILTER_OPERATORS: FilterOperator[] = [
  { value: "contains", label: "contains", inputType: "text" },
  { value: "equals", label: "equals", inputType: "text" },
  { value: "starts_with", label: "starts with", inputType: "text" },
  { value: "ends_with", label: "ends with", inputType: "text" },
];

const FIELD_EXISTS_OPERATORS: FilterOperator[] = [
  { value: "exists", label: "exists" },
  { value: "does_not_exist", label: "does not exist" },
];

const FIELD_EXISTS_OPERATOR_VALUES = new Set(
  FIELD_EXISTS_OPERATORS.map((op) => op.value)
);

function isTextFieldFilterType(filterType: string): boolean {
  return (
    filterType === "firstName" ||
    filterType === "lastName" ||
    filterType === "email" ||
    filterType === "phoneNumber" ||
    filterType === "instagramUsername"
  );
}

const FILTER_TYPES: FilterType[] = [
  {
    value: "firstName",
    label: "First name",
    operators: [...TEXT_FIELD_FILTER_OPERATORS, ...FIELD_EXISTS_OPERATORS],
  },
  {
    value: "lastName",
    label: "Last name",
    operators: [...TEXT_FIELD_FILTER_OPERATORS, ...FIELD_EXISTS_OPERATORS],
  },
  {
    value: "email",
    label: "Email",
    operators: [...TEXT_FIELD_FILTER_OPERATORS, ...FIELD_EXISTS_OPERATORS],
  },
  {
    value: "phoneNumber",
    label: "Phone number",
    operators: [...TEXT_FIELD_FILTER_OPERATORS, ...FIELD_EXISTS_OPERATORS],
  },
  {
    value: "instagramUsername",
    label: "Instagram username",
    operators: [...TEXT_FIELD_FILTER_OPERATORS, ...FIELD_EXISTS_OPERATORS],
  },
  {
    value: "city",
    label: "City",
    operators: [
      {
        value: "is_within",
        label: "is within",
        inputType: "number",
        inputLabel: "Distance",
        inputUnit: "miles",
      },
    ],
  },
  {
    value: "page",
    label: "Page",
    operators: [
      { value: "has_submitted", label: "has submitted" },
      { value: "has_not_submitted", label: "has not submitted" },
    ],
  },
  {
    value: "countryCode",
    label: "Country Code",
    operators: [],
  },
  {
    value: "contactGroup",
    label: "Contact Group",
    operators: [],
  },
  {
    value: "product",
    label: "Product",
    operators: [
      { value: "has_purchased", label: "Has purchased product" },
      { value: "has_not_purchased", label: "Has not purchased product" },
      { value: "has_pre_ordered", label: "Has pre-ordered product" },
    ],
  },
  {
    value: "numPurchases",
    label: "Number of purchases",
    operators: [
      {
        value: "gte",
        label: "at least",
        inputType: "number",
        inputLabel: "Count",
      },
      {
        value: "lte",
        label: "at most",
        inputType: "number",
        inputLabel: "Count",
      },
      { value: "eq", label: "equals", inputType: "number", inputLabel: "Count" },
      {
        value: "gt",
        label: "greater than",
        inputType: "number",
        inputLabel: "Count",
      },
      {
        value: "lt",
        label: "less than",
        inputType: "number",
        inputLabel: "Count",
      },
    ],
  },
  {
    value: "amountSpent",
    label: "Amount spent",
    operators: [
      {
        value: "gte",
        label: "at least",
        inputType: "number",
        inputLabel: "Amount",
      },
      {
        value: "lte",
        label: "at most",
        inputType: "number",
        inputLabel: "Amount",
      },
      { value: "eq", label: "equals", inputType: "number", inputLabel: "Amount" },
      {
        value: "gt",
        label: "greater than",
        inputType: "number",
        inputLabel: "Amount",
      },
      {
        value: "lt",
        label: "less than",
        inputType: "number",
        inputLabel: "Amount",
      },
    ],
  },
  {
    value: "lastActive",
    label: "Last active",
    operators: [
      {
        value: "gte",
        label: "on or after",
        inputType: "date",
        inputLabel: "Date",
      },
      {
        value: "lte",
        label: "on or before",
        inputType: "date",
        inputLabel: "Date",
      },
      { value: "eq", label: "on", inputType: "date", inputLabel: "Date" },
      { value: "gt", label: "after", inputType: "date", inputLabel: "Date" },
      { value: "lt", label: "before", inputType: "date", inputLabel: "Date" },
      ...FIELD_EXISTS_OPERATORS,
    ],
  },
];

const COUNTRY_OPTIONS: { value: string; label: string }[] = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
  { value: "GH", label: "Ghana" },
  { value: "NG", label: "Nigeria" },
  { value: "ZA", label: "South Africa" },
  { value: "BR", label: "Brazil" },
  { value: "CO", label: "Colombia" },
];

export type FilterProductOption = ProductCatalogOption;

export type FilterItem = {
  id: string;
  filterType: string;
  operator?: string;
  filterValue?: string;
  locationLat?: number;
  locationLng?: number;
  pageId?: string;
  /** PageResponse.formHtmlId — limit page submission filter to one form on the page. */
  formHtmlId?: string;
  countryCodes?: string[];
  groupId?: string;
  productId?: string;
  variantId?: string;
};

type Lead = {
  _id: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
};
type Page = { _id: string; title: string; route: string; formHtmlIds?: string[] };
type Group = { _id: string; name: string; contacts?: number };

export type RecipientsFilterProps = {
  filters: FilterItem[];
  onFiltersChange: (filters: FilterItem[]) => void;
  pages: Page[];
  groups: Group[];
  leads: Lead[];
  selectedLeadIds: Set<string>;
  onSelectLead: (leadId: string) => void;
  onSelectAll: () => void;
  contactField?: "phone" | "email";
  /** When false, only filter rows are shown (no recipient checkboxes table). */
  showLeadPicker?: boolean;
  products?: FilterProductOption[];
  filtersOperator?: FiltersOperator;
  onFiltersOperatorChange?: (operator: FiltersOperator) => void;
  csvPhoneUpload?: {
    uploadedCount: number;
    matchedCount: number;
    skippedCount: number;
    isParsing: boolean;
    onUpload: (file: File) => void;
    onClear: () => void;
  };
};

function FilterRow({
  filter,
  onUpdate,
  onRemove,
  canRemove,
  countryCodeFilterExists,
  pages,
  groups,
  products,
}: {
  filter: FilterItem;
  onUpdate: (updated: FilterItem) => void;
  onRemove: () => void;
  canRemove: boolean;
  countryCodeFilterExists: boolean;
  pages: Page[];
  groups: Group[];
  products: FilterProductOption[];
}) {
  const selectedPage =
    filter.filterType === "page" && filter.pageId
      ? pages.find((p) => p._id === filter.pageId)
      : undefined;
  const pageFormIds = selectedPage?.formHtmlIds ?? [];
  const currentFilterType = FILTER_TYPES.find((f) => f.value === filter.filterType);
  const currentOperator = currentFilterType?.operators.find((o) => o.value === filter.operator);
  const selectedProduct =
    filter.filterType === "product" && filter.productId
      ? products.find((p) => p._id === filter.productId)
      : undefined;
  const productVariants = selectedProduct?.variants?.filter((v) => v._id) ?? [];

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 space-y-4">
          <div className="space-y-2">
            <Label>Filter type</Label>
            <Select
              value={filter.filterType || " "}
              onValueChange={(value) =>
                onUpdate({
                  ...filter,
                  filterType: value === " " ? "" : value,
                  operator:
                    value === "page"
                      ? "has_submitted"
                      : value === "product"
                        ? "has_purchased"
                        : value === "numPurchases" ||
                            value === "amountSpent" ||
                            value === "lastActive"
                          ? "gte"
                          : isTextFieldFilterType(value)
                            ? "contains"
                            : undefined,
                  filterValue: undefined,
                  locationLat: undefined,
                  locationLng: undefined,
                  pageId: undefined,
                  formHtmlId: undefined,
                  countryCodes: value === "countryCode" ? ["US", "CA"] : undefined,
                  groupId: undefined,
                  productId: undefined,
                  variantId: undefined,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">
                  Choose a filter
                </SelectItem>
                {FILTER_TYPES.filter(
                  (f) => f.value !== "countryCode" || !countryCodeFilterExists || filter.filterType === "countryCode"
                ).map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {currentFilterType &&
            filter.filterType !== "page" &&
            filter.filterType !== "countryCode" &&
            filter.filterType !== "contactGroup" &&
            filter.filterType !== "product" &&
            filter.filterType !== "numPurchases" &&
            filter.filterType !== "amountSpent" &&
            filter.filterType !== "lastActive" && (
            <div className="space-y-2">
              <Label>Operator</Label>
              <Select
                value={filter.operator ?? ""}
                onValueChange={(value) => onUpdate({ ...filter, operator: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an operator" />
                </SelectTrigger>
                <SelectContent>
                  {currentFilterType.operators.map((op) => (
                    <SelectItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isTextFieldFilterType(filter.filterType) &&
          filter.operator &&
          !FIELD_EXISTS_OPERATOR_VALUES.has(filter.operator) ? (
            <div className="space-y-2">
              <Label>Value</Label>
              <Input
                type="text"
                value={filter.filterValue ?? ""}
                onChange={(e) => onUpdate({ ...filter, filterValue: e.target.value })}
                placeholder={
                  filter.filterType === "email"
                    ? "email@example.com"
                    : filter.filterType === "phoneNumber"
                      ? "+1… or partial number"
                      : filter.filterType === "instagramUsername"
                        ? "@username"
                        : "Text to match"
                }
              />
            </div>
          ) : null}

          {filter.filterType === "countryCode" && (
            <div className="space-y-2">
              <Label>Select countries</Label>
              <div className="flex flex-wrap gap-3">
                {COUNTRY_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`${filter.id}-country-${opt.value}`}
                      checked={(filter.countryCodes ?? []).includes(opt.value)}
                      onCheckedChange={(checked) => {
                        const codes = filter.countryCodes ?? [];
                        onUpdate({
                          ...filter,
                          countryCodes: checked
                            ? [...codes, opt.value]
                            : codes.filter((c) => c !== opt.value),
                        });
                      }}
                    />
                    <label
                      htmlFor={`${filter.id}-country-${opt.value}`}
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      {opt.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filter.filterType === "contactGroup" && (
            <div className="space-y-2">
              <Label>Select group</Label>
              <Select
                value={filter.groupId ?? ""}
                onValueChange={(value) => onUpdate({ ...filter, groupId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((group) => (
                    <SelectItem key={group._id} value={group._id}>
                      {group.name}
                      {group.contacts != null ? ` (${group.contacts})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {filter.filterType === "product" && (
            <>
              <div className="space-y-2">
                <Label>Condition</Label>
                <Select
                  value={filter.operator ?? ""}
                  onValueChange={(value) => onUpdate({ ...filter, operator: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose condition" />
                  </SelectTrigger>
                  <SelectContent>
                    {(FILTER_TYPES.find((ft) => ft.value === "product")?.operators ?? []).map(
                      (op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
              {(filter.operator === "has_purchased" ||
                filter.operator === "has_not_purchased" ||
                filter.operator === "has_pre_ordered") && (
                <>
                  <div className="space-y-2">
                    <Label>Product</Label>
                    <Select
                      value={filter.productId ?? ""}
                      onValueChange={(value) =>
                        onUpdate({ ...filter, productId: value, variantId: undefined })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a product" />
                      </SelectTrigger>
                      <SelectContent>
                        <ProductSelectItems
                          products={products}
                          selectedProductId={filter.productId ?? undefined}
                          getLabel={(product) => product.name?.trim() || "Untitled product"}
                        />
                      </SelectContent>
                    </Select>
                  </div>
                  {productVariants.length > 0 && (
                    <div className="space-y-2">
                      <Label>Variant</Label>
                      <Select
                        value={filter.variantId ?? "__any__"}
                        onValueChange={(value) =>
                          onUpdate({
                            ...filter,
                            variantId: value === "__any__" ? undefined : value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any variant" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__any__">Any variant</SelectItem>
                          {productVariants.map((v) => (
                            <SelectItem key={v._id} value={String(v._id)}>
                              {v.name?.trim() || "Variant"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {filter.filterType === "page" && (
            <>
              <div className="space-y-2">
                <Label>Condition</Label>
                <Select
                  value={filter.operator ?? ""}
                  onValueChange={(value) => onUpdate({ ...filter, operator: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose condition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="has_submitted">has submitted</SelectItem>
                    <SelectItem value="has_not_submitted">has not submitted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Page</Label>
                <Select
                  value={filter.pageId ?? ""}
                  onValueChange={(value) =>
                    onUpdate({ ...filter, pageId: value, formHtmlId: undefined })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a page" />
                  </SelectTrigger>
                  <SelectContent>
                    {pages.map((page) => (
                      <SelectItem key={page._id} value={page._id}>
                        {page.title || page.route}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {filter.pageId ? (
                <div className="space-y-2">
                  <Label>Form ID (optional)</Label>
                  <Input
                    list={
                      pageFormIds.length > 0 ? `page-form-ids-${filter.id}` : undefined
                    }
                    placeholder={
                      pageFormIds.length > 0
                        ? "Leave blank for any form, or pick / type an id"
                        : "Leave blank for any form, or enter a form HTML id"
                    }
                    value={filter.formHtmlId ?? ""}
                    onChange={(e) =>
                      onUpdate({
                        ...filter,
                        formHtmlId: e.target.value.trim()
                          ? e.target.value
                          : undefined,
                      })
                    }
                  />
                  {pageFormIds.length > 0 ? (
                    <datalist id={`page-form-ids-${filter.id}`}>
                      {pageFormIds.map((id) => (
                        <option key={id} value={id} />
                      ))}
                    </datalist>
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    Matches submissions recorded with this{" "}
                    <code className="text-xs">formHtmlId</code> on PageResponse (HTML forms and
                    form-specific flows).
                  </p>
                </div>
              ) : null}
            </>
          )}

          {(filter.filterType === "numPurchases" || filter.filterType === "amountSpent") && (
            <>
              <div className="space-y-2">
                <Label>Operator</Label>
                <Select
                  value={filter.operator ?? ""}
                  onValueChange={(value) => onUpdate({ ...filter, operator: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {(FILTER_TYPES.find((ft) => ft.value === filter.filterType)?.operators ?? []).map(
                      (op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>
                  {filter.filterType === "numPurchases"
                    ? "Purchase count"
                    : "Total amount spent"}
                </Label>
                <Input
                  type="number"
                  value={filter.filterValue ?? ""}
                  onChange={(e) => onUpdate({ ...filter, filterValue: e.target.value })}
                  placeholder={filter.filterType === "numPurchases" ? "0" : "0.00"}
                  min="0"
                  step={filter.filterType === "numPurchases" ? "1" : "0.01"}
                />
                {filter.filterType === "amountSpent" && (
                  <p className="text-xs text-muted-foreground">
                    Enter the amount in dollars.
                  </p>
                )}
              </div>
            </>
          )}

          {filter.filterType === "lastActive" && (
            <>
              <div className="space-y-2">
                <Label>Operator</Label>
                <Select
                  value={filter.operator ?? ""}
                  onValueChange={(value) =>
                    onUpdate({
                      ...filter,
                      operator: value,
                      filterValue: FIELD_EXISTS_OPERATOR_VALUES.has(value)
                        ? undefined
                        : filter.filterValue,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {(FILTER_TYPES.find((ft) => ft.value === "lastActive")?.operators ?? []).map(
                      (op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
              {filter.operator &&
              !FIELD_EXISTS_OPERATOR_VALUES.has(filter.operator) ? (
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={filter.filterValue ?? ""}
                    onChange={(e) =>
                      onUpdate({ ...filter, filterValue: e.target.value })
                    }
                  />
                </div>
              ) : null}
            </>
          )}

          {filter.filterType === "city" && filter.operator === "is_within" && (
            <>
              <div className="space-y-2">
                <Label>Location</Label>
                <MapPickerWithRadius
                  lat={filter.locationLat}
                  lng={filter.locationLng}
                  radiusMiles={filter.filterValue ? parseFloat(filter.filterValue) : undefined}
                  onLocationSelect={(lat, lng) =>
                    onUpdate({ ...filter, locationLat: lat, locationLng: lng })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Distance (miles)</Label>
                <Input
                  type="number"
                  value={filter.filterValue ?? ""}
                  onChange={(e) => onUpdate({ ...filter, filterValue: e.target.value })}
                  placeholder="Enter distance"
                  min="0"
                  step="0.1"
                />
              </div>
            </>
          )}
        </div>
        {canRemove && (
          <Button variant="ghost" size="icon" onClick={onRemove} className="shrink-0 text-muted-foreground hover:text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function RecipientsFilterInner({
  filters,
  onFiltersChange,
  pages,
  groups,
  leads,
  selectedLeadIds,
  onSelectLead,
  onSelectAll,
  contactField = "phone",
  showLeadPicker = true,
  products = [],
  filtersOperator = DEFAULT_FILTERS_OPERATOR,
  onFiltersOperatorChange,
  csvPhoneUpload,
}: RecipientsFilterProps) {
  const [isTableOpen, setIsTableOpen] = useState(false);
  const isSelectAll = leads.length > 0 && leads.every((l) => selectedLeadIds.has(l._id));

  const handleAddFilter = () => {
    onFiltersChange([
      ...filters,
      {
        id: crypto.randomUUID(),
        filterType: "",
      },
    ]);
  };

  const handleUpdateFilter = (index: number, updated: FilterItem) => {
    const next = [...filters];
    next[index] = updated;
    onFiltersChange(next);
  };

  const handleRemoveFilter = (index: number) => {
    onFiltersChange(filters.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4 pt-2">
      <h2 className="text-lg font-semibold">
        {showLeadPicker ? "Recipients" : "Filters"}
      </h2>

      <div className="space-y-4">
        {filters.length >= 2 && onFiltersOperatorChange && (
          <div className="space-y-2">
            <Label>Recipients must match</Label>
            <Select
              value={filtersOperator}
              onValueChange={(value) =>
                onFiltersOperatorChange(value as FiltersOperator)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="and">All filters (AND)</SelectItem>
                <SelectItem value="or">Any filter (OR)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {filters.map((filter, index) => (
          <FilterRow
            key={filter.id}
            filter={filter}
            onUpdate={(updated) => handleUpdateFilter(index, updated)}
            onRemove={() => handleRemoveFilter(index)}
            canRemove
            countryCodeFilterExists={filters.some((f) => f.filterType === "countryCode")}
            pages={pages}
            groups={groups}
            products={products}
          />
        ))}

        <Button variant="outline" onClick={handleAddFilter} className="w-full">
          Add filter
        </Button>

        {showLeadPicker && contactField === "phone" && csvPhoneUpload ? (
          <RecipientPhoneCsvUpload {...csvPhoneUpload} />
        ) : null}

        {showLeadPicker && leads.length > 0 && (
          <div className="pt-4 space-y-2">
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsTableOpen((prev) => !prev)}
                className="flex items-center gap-1.5 text-xs"
              >
                {isTableOpen ? (
                  <>
                    <ChevronUp className="h-3.5 w-3.5" />
                    Hide recipient list
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3.5 w-3.5" />
                    Show recipient list ({leads.length.toLocaleString()})
                  </>
                )}
              </Button>
            </div>

            {isTableOpen && (
              <div className="border rounded-md max-h-64 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox checked={isSelectAll} onCheckedChange={onSelectAll} />
                      </TableHead>
                      <TableHead>First name</TableHead>
                      <TableHead>Last name</TableHead>
                      <TableHead>{contactField === "email" ? "Email" : "Phone"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead._id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedLeadIds.has(lead._id)}
                            onCheckedChange={() => onSelectLead(lead._id)}
                          />
                        </TableCell>
                        <TableCell>{lead.firstName || "-"}</TableCell>
                        <TableCell>{lead.lastName || "-"}</TableCell>
                        <TableCell>
                          {(contactField === "email" ? lead.email : lead.phoneNumber) || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const RecipientsFilter = memo(RecipientsFilterInner);
